import React from 'react'
import {
  Alert,
  Button,
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'
import { usePageTitle } from '../../components/common/PageTitleContext'
import { grantCredits, grantEntitlement } from '../../api/superadminGrants'

type NoticeState = {
  type: 'success' | 'error'
  message: string
}

type EntitlementFormState = {
  subjectType: 'user' | 'station'
  subjectId: string
  featureKey: string
  expiresAt: string
  note: string
}

type CreditsFormState = {
  ownerType: 'user' | 'station'
  ownerId: string
  bucket: 'included' | 'subscription' | 'pack' | 'manual'
  amount: string
  expiresAt: string
  note: string
}

const FEATURE_OPTIONS = [
  { value: 'prep_sheet', label: 'Prep sheet' },
  { value: 'show_clock', label: 'Show clock' },
  { value: 'unified_inbox', label: 'Unified inbox' },
  { value: 'youtube_downloader', label: 'YouTube downloader' },
]

const defaultEntitlementState: EntitlementFormState = {
  subjectType: 'user',
  subjectId: '',
  featureKey: 'prep_sheet',
  expiresAt: '',
  note: '',
}

const defaultCreditsState: CreditsFormState = {
  ownerType: 'station',
  ownerId: '',
  bucket: 'manual',
  amount: '',
  expiresAt: '',
  note: '',
}

const parseNumber = (value: string): number => {
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : 0
}

const toIsoString = (value: string): string | null => {
  if (!value) {
    return null
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return parsed.toISOString()
}

const ManualGrantsPage: React.FC = () => {
  usePageTitle('Manual grants')

  const [entitlementForm, setEntitlementForm] = React.useState<EntitlementFormState>(defaultEntitlementState)
  const [creditsForm, setCreditsForm] = React.useState<CreditsFormState>(defaultCreditsState)
  const [entitlementNotice, setEntitlementNotice] = React.useState<NoticeState | null>(null)
  const [creditsNotice, setCreditsNotice] = React.useState<NoticeState | null>(null)
  const [entitlementSaving, setEntitlementSaving] = React.useState(false)
  const [creditsSaving, setCreditsSaving] = React.useState(false)

  const handleSubjectTypeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value === 'station' ? 'station' : 'user'
    setEntitlementForm((prev) => ({ ...prev, subjectType: value }))
  }

  const handleFeatureChange = (event: SelectChangeEvent<string>) => {
    setEntitlementForm((prev) => ({ ...prev, featureKey: event.target.value }))
  }

  const handleOwnerTypeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value === 'user' ? 'user' : 'station'
    setCreditsForm((prev) => ({ ...prev, ownerType: value }))
  }

  const handleBucketChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as CreditsFormState['bucket']
    setCreditsForm((prev) => ({ ...prev, bucket: value }))
  }

  const handleEntitlementSubmit = async () => {
    setEntitlementNotice(null)

    const subjectId = parseNumber(entitlementForm.subjectId)
    if (subjectId <= 0) {
      setEntitlementNotice({ type: 'error', message: 'Enter a valid subject ID.' })
      return
    }

    const expiresAt = toIsoString(entitlementForm.expiresAt)
    if (entitlementForm.expiresAt && !expiresAt) {
      setEntitlementNotice({ type: 'error', message: 'Enter a valid expiry date.' })
      return
    }

    setEntitlementSaving(true)

    try {
      await grantEntitlement({
        subject_type: entitlementForm.subjectType,
        subject_id: subjectId,
        feature_key: entitlementForm.featureKey,
        expires_at: expiresAt ?? undefined,
        note: entitlementForm.note.trim() || undefined,
      })

      setEntitlementNotice({ type: 'success', message: 'Entitlement granted successfully.' })
      setEntitlementForm((prev) => ({
        ...prev,
        subjectId: '',
        expiresAt: '',
        note: '',
      }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to grant entitlement.'
      setEntitlementNotice({ type: 'error', message })
    } finally {
      setEntitlementSaving(false)
    }
  }

  const handleCreditsSubmit = async () => {
    setCreditsNotice(null)

    const ownerId = parseNumber(creditsForm.ownerId)
    if (ownerId <= 0) {
      setCreditsNotice({ type: 'error', message: 'Enter a valid owner ID.' })
      return
    }

    const amount = parseNumber(creditsForm.amount)
    if (amount <= 0) {
      setCreditsNotice({ type: 'error', message: 'Enter a credit amount greater than zero.' })
      return
    }

    const expiresAt = toIsoString(creditsForm.expiresAt)
    if (creditsForm.expiresAt && !expiresAt) {
      setCreditsNotice({ type: 'error', message: 'Enter a valid expiry date.' })
      return
    }

    setCreditsSaving(true)

    try {
      await grantCredits({
        owner_type: creditsForm.ownerType,
        owner_id: ownerId,
        bucket: creditsForm.bucket,
        amount,
        expires_at: expiresAt ?? undefined,
        note: creditsForm.note.trim() || undefined,
      })

      setCreditsNotice({ type: 'success', message: 'Credits granted successfully.' })
      setCreditsForm((prev) => ({
        ...prev,
        ownerId: '',
        amount: '',
        expiresAt: '',
        note: '',
      }))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to grant credits.'
      setCreditsNotice({ type: 'error', message })
    } finally {
      setCreditsSaving(false)
    }
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h5" component="h2">
          Manual grants
        </Typography>
        <Typography color="text.secondary">
          Issue one-off entitlements or credit top-ups for a specific user or station.
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={3}>
          <Stack spacing={0.5}>
            <Typography variant="h6">Grant entitlement</Typography>
            <Typography variant="body2" color="text.secondary">
              Applies access for a single feature. Use a future expiry to run temporary promotions.
            </Typography>
          </Stack>

          {entitlementNotice ? <Alert severity={entitlementNotice.type}>{entitlementNotice.message}</Alert> : null}

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl sx={{ minWidth: { xs: '100%', md: 180 } }}>
                <FormLabel>Subject type</FormLabel>
                <Select value={entitlementForm.subjectType} onChange={handleSubjectTypeChange} size="small">
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="station">Station</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Subject ID"
                value={entitlementForm.subjectId}
                onChange={(event) =>
                  setEntitlementForm((prev) => ({
                    ...prev,
                    subjectId: event.target.value,
                  }))
                }
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                helperText="Enter the user ID or station ID."
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ minWidth: { xs: '100%', md: 220 } }}>
                <FormLabel>Feature</FormLabel>
                <Select value={entitlementForm.featureKey} onChange={handleFeatureChange} size="small">
                  {FEATURE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Expires at (optional)"
                value={entitlementForm.expiresAt}
                onChange={(event) =>
                  setEntitlementForm((prev) => ({
                    ...prev,
                    expiresAt: event.target.value,
                  }))
                }
                size="small"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: '100%', md: 260 } }}
              />
              <TextField
                label="Note (optional)"
                value={entitlementForm.note}
                onChange={(event) =>
                  setEntitlementForm((prev) => ({
                    ...prev,
                    note: event.target.value,
                  }))
                }
                size="small"
                multiline
                minRows={2}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" onClick={handleEntitlementSubmit} disabled={entitlementSaving}>
              {entitlementSaving ? 'Granting...' : 'Grant entitlement'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Divider />

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={3}>
          <Stack spacing={0.5}>
            <Typography variant="h6">Grant credits</Typography>
            <Typography variant="body2" color="text.secondary">
              Add credits to the YouTube downloader buckets. Use manual for one-off top-ups.
            </Typography>
          </Stack>

          {creditsNotice ? <Alert severity={creditsNotice.type}>{creditsNotice.message}</Alert> : null}

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl sx={{ minWidth: { xs: '100%', md: 180 } }}>
                <FormLabel>Owner type</FormLabel>
                <Select value={creditsForm.ownerType} onChange={handleOwnerTypeChange} size="small">
                  <MenuItem value="station">Station</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Owner ID"
                value={creditsForm.ownerId}
                onChange={(event) =>
                  setCreditsForm((prev) => ({
                    ...prev,
                    ownerId: event.target.value,
                  }))
                }
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                helperText="Enter the station ID or user ID."
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }}>
                <FormLabel>Bucket</FormLabel>
                <Select value={creditsForm.bucket} onChange={handleBucketChange} size="small">
                  <MenuItem value="manual">Manual top-up</MenuItem>
                  <MenuItem value="included">Included</MenuItem>
                  <MenuItem value="subscription">Subscription</MenuItem>
                  <MenuItem value="pack">Credit pack</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Amount"
                value={creditsForm.amount}
                onChange={(event) =>
                  setCreditsForm((prev) => ({
                    ...prev,
                    amount: event.target.value,
                  }))
                }
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                sx={{ minWidth: { xs: '100%', md: 160 } }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Expires at (optional)"
                value={creditsForm.expiresAt}
                onChange={(event) =>
                  setCreditsForm((prev) => ({
                    ...prev,
                    expiresAt: event.target.value,
                  }))
                }
                size="small"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: '100%', md: 260 } }}
              />
              <TextField
                label="Note (optional)"
                value={creditsForm.note}
                onChange={(event) =>
                  setCreditsForm((prev) => ({
                    ...prev,
                    note: event.target.value,
                  }))
                }
                size="small"
                multiline
                minRows={2}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" onClick={handleCreditsSubmit} disabled={creditsSaving}>
              {creditsSaving ? 'Granting...' : 'Grant credits'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  )
}

export default ManualGrantsPage
